import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@components/shared/button/Button';
import {
  getIsLoggedIn,
  getUserInfo,
  getAuthInitialized,
} from '@store/auth/auth-selectors';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@configs/routeConstants';
import {
  acceptOperation,
  deferOperation,
  notifyShownOperation,
} from '@store/surveyInvite/surveyInvite-operations';
import {
  getSurveyInviteVisible,
  getSurveyInviteResolved,
} from '@store/surveyInvite/surveyInvite-selectors';
import { setShow } from '@store/surveyInvite/surveyInvite-slice';
import type { AppDispatch } from 'store/store';

import s from '@components/shared/renewTest/RenewTest.module.scss';
import useMetrics from '@hooks/useMetrics';
import { METRIC_EVENTS, METRIC_EVENTS_TRIGGERS } from '@configs/metrics/events';
import { useModalFromQuery } from '@hooks/useModalFromQuery';
import T from '@data/text/sharedTexts.json';

const containerStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const SurveyInviteModal: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const initialized = useSelector(getAuthInitialized);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const me = useSelector(getUserInfo);
  const visible = useSelector(getSurveyInviteVisible);
  const resolved = useSelector(getSurveyInviteResolved);

  const { trackEvent } = useMetrics();
  const triggerSource = METRIC_EVENTS_TRIGGERS.myHealth;

  const { close: closeFromQuery } = useModalFromQuery(
    'surveyInvite',
    (open) => dispatch(setShow(open))
  );

  useEffect(() => {
    if (!initialized) return;
    if (!isLoggedIn || !me) return;

    const need = me.current_program?.need_survey_notification === true;
    if (need && !resolved) {
      dispatch(setShow(true));
    }
  }, [initialized, isLoggedIn, me, resolved, dispatch]);

  const notifiedRef = useRef(false);
  const markNotifiedOnce = () => {
    if (notifiedRef.current) return;
    notifiedRef.current = true;
    dispatch(notifyShownOperation());
  };

  const screenTrackedRef = useRef(false);
  useEffect(() => {
    if (visible && !screenTrackedRef.current) {
      screenTrackedRef.current = true;
      trackEvent(METRIC_EVENTS.test_retry_screen, triggerSource);
    }
    if (!visible) {
      screenTrackedRef.current = false;
    }
  }, [visible, trackEvent]);

  if (!visible) return null;

  const onAccept = () => {
    markNotifiedOnce();
    trackEvent(METRIC_EVENTS.test_retry_click, triggerSource, {
      is_agreed: true,
    });

    const withOrder = false;

    dispatch(acceptOperation())
      .finally(() => {
        const url = withOrder
          ? `${ROUTES.questionnaire}?mode=renew&withOrder=1`
          : `${ROUTES.questionnaire}?mode=renew`;
        closeFromQuery();
        navigate(url);
      });

  };

  const onDefer = () => {
    markNotifiedOnce();
    trackEvent(METRIC_EVENTS.test_retry_click, triggerSource, {
      is_agreed: false,
    });

    closeFromQuery();
    dispatch(deferOperation());
  };

  return (
    <div style={containerStyle} aria-live="polite">
      <button
        type="button"
        className={s.modalOverlay}
        aria-label={T.surveyInviteModal.overlayAriaLabel}
        onClick={onDefer}
      />
      <div className={s.modal} role="dialog" aria-modal="true">
        <h3 className={s.modalTitle}>{T.surveyInviteModal.title}</h3>
        <p className={s.modalText}>{T.surveyInviteModal.text}</p>
        <div className={s.modalActions}>
          <Button variant="primary" onClick={onAccept}>
            {T.surveyInviteModal.confirm}
          </Button>
          <Button variant="link" onClick={onDefer}>
            {T.surveyInviteModal.later}
          </Button>
        </div>
      </div>
    </div>
  );
};
